import { useCallback, useState } from "react"
import moment from "moment"
import { loadStripe } from "@stripe/stripe-js"
import { STRIPE_PUBLISHABLE_KEY } from "../../../data/constants/apiConstants"
import { useGetFastTrackUserOnMount } from "../../../fast-track/hooks/useGetFastTrackUserOnMount"
import { useNewSubscriptionStepper } from "../../../common/hooks/subscription/useNewSubscriptionStepper"

// Load Stripe.js eagerly at module level — no API round-trip needed
const stripePromiseEager = loadStripe(STRIPE_PUBLISHABLE_KEY)

/**
 * Cap a date at the 1st of next month
 */
const capStartDate = (date) => {
    const firstOfNextMonth = moment().add(1, "month").startOf("month")
    return date.isAfter(firstOfNextMonth) ? firstOfNextMonth : date
}

/**
 * Calculate the subscription start date (48h from now, skipping Sunday)
 * - If landing on Sunday, move to Monday
 * - Cannot exceed the 1st of next month
 */
const getSubscriptionStartDate = () => {
    let startDate = moment().add(48, "hours")
    // If it's Sunday (day 0), move to Monday
    if (startDate.day() === 0) {
        startDate.add(1, "day")
    }
    // Cap at 1st of next month
    return capStartDate(startDate).format("YYYY-MM-DD")
}

export const useCreateNewSubscriptionState = (props, hasInvoice) => {
    const {
        generateDefaultSubjectHoursAllocation,
        getHoursPerWeekFromHoursPerMonth,
    } = useNewSubscriptionStepper()
    const [plan, setPlan] = useState({
        startDate: getSubscriptionStartDate(),
        offerType: "essential",
        subjectIds: [],
        hoursPerWeek: 2,
        hoursAllocation: {},
        engagementDuration: 12,
        withCommitment: true,
    })

    const {
        initialLoading,
        email,
        emailB64,
        user,
        state: fastTrackData,
        subscriptionMetadata,
    } = useGetFastTrackUserOnMount(props, (data) => {
        const { user, subscription_metadata, recommended_hours_per_week } = data

        const subjects = user?.prioritized_school_subjects
        const hoursPerWeek = hasInvoice
            ? getHoursPerWeekFromHoursPerMonth(
                  props.sessionsPerWeek,
                  subscription_metadata?.hours_per_month
              )
            : Number(recommended_hours_per_week) || 2
        const hoursAllocation = generateDefaultSubjectHoursAllocation(
            subjects?.map((x) => x.id),
            hoursPerWeek
        )
        handleChangePlan({
            ...plan,
            subjectIds: subjects?.length ? subjects?.map((x) => x.id) : [1],
            hoursPerWeek,
            hoursAllocation,
            ...(hasInvoice
                ? {
                      offerType:
                          subscription_metadata?.offer_type || plan.offerType,
                      withCommitment:
                          subscription_metadata?.with_commitment ??
                          plan.withCommitment,
                      // Use starts_at from metadata when invoice exists (admin decision, no cap)
                      startDate: moment(
                          subscription_metadata?.starts_at
                      ).format("YYYY-MM-DD"),
                  }
                : {}),
        })
    })

    const handleChangePlan = useCallback(
        (newPlan) => {
            if (JSON.stringify(plan) !== JSON.stringify(newPlan))
                setPlan(newPlan)
        },
        [plan]
    )

    return {
        stripePromise: stripePromiseEager,
        plan,
        handleChangePlan,
        loadingUser: initialLoading || !plan.subjectIds.length, // If no subjects, we're loading
        email,
        emailB64,
        user,
        fastTrackData,
        subscriptionMetadata,
    }
}
